import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as tradeYmmtSelectors from '@makemydeal/dr-dash-store/selectors/tradeYmmtSelectors';
import { showIncompleteTradeModal } from '@makemydeal/dr-dash-store/actions/incompleteTradeModalActionCreators';

/**
 * Hook that provides a pre-flight guard for save-offer flows.
 *
 * Reads the current incomplete trade state from Redux and returns:
 *  - `wrap(action, uiOnlyAction?)` — HOF that returns a `() => void`. When called,
 *    if the trade is incomplete it stores the pending callback and shows the
 *    Incomplete Trade modal; otherwise it calls `action()` immediately.
 *    Pass `uiOnlyAction` for flows with a payment-orchestrator step: when the modal
 *    intercepts, `uiOnlyAction` (not `action`) is stored so that `saveOffer()` is
 *    handled by the middleware on `PAYMENT_RECEIVED` rather than being called twice.
 *  - `onContinue` — pass as the `onContinue` prop to `<IncompleteTradeModal>`.
 *    Executes the stored callback after the user clicks Continue.
 *
 * Uses `useRef` (not `useState`) for the pending callback so that storing a
 * function does not trigger a re-render and avoids the React double-arrow updater
 * footgun (`setState(() => () => fn())`).
 */
export const useIncompleteTradeGuard = () => {
    const dispatch = useDispatch();
    const shouldShowForMultiple = useSelector(tradeYmmtSelectors.shouldShowIncompleteTradeModalForMultiple);

    const pendingCallbackRef = useRef<(() => void) | null>(null);

    const wrap = useCallback(
        (action: () => void, uiOnlyAction?: () => void): (() => void) =>
            () => {
                if (shouldShowForMultiple) {
                    pendingCallbackRef.current = uiOnlyAction ?? action;
                    dispatch(showIncompleteTradeModal());
                } else {
                    action();
                }
            },
        [shouldShowForMultiple, dispatch]
    );

    const onContinue = useCallback(() => {
        pendingCallbackRef.current?.();
        pendingCallbackRef.current = null;
    }, []);

    return useMemo(() => ({ wrap, onContinue }), [wrap, onContinue]);
};
